'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface VideoPlayerProps {
  isLive: boolean;
  streamUrl: string;
  recordingUrl?: string;
}

const VideoPlayer = ({ isLive, streamUrl, recordingUrl }: VideoPlayerProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [volume, setVolume] = useState(80);
  const [isMuted, setIsMuted] = useState(false);
  const [quality, setQuality] = useState<'auto' | '1080p' | '720p' | '480p'>('auto');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="relative w-full aspect-video bg-secondary rounded-lg overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  return (
    <div className={`relative w-full ${isFullscreen ? 'fixed inset-0 z-50' : 'aspect-video'} bg-secondary rounded-lg overflow-hidden`}>
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-secondary to-slate-800">
        <div className="text-center space-y-4">
          <Icon name="VideoCameraIcon" size={64} variant="solid" className="text-primary mx-auto" />
          <p className="text-white text-lg font-medium">
            {isLive ? 'Live Stream Active' : 'Recording Playback'}
          </p>
          <p className="text-slate-300 text-sm">Video streaming interface</p>
        </div>
      </div>

      {isLive && (
        <div className="absolute top-4 left-4 px-3 py-1 bg-error rounded-full flex items-center space-x-2 animate-pulse">
          <div className="w-2 h-2 bg-white rounded-full"></div>
          <span className="text-white text-sm font-semibold">LIVE</span>
        </div>
      )}

      <div className="absolute top-4 right-4 flex items-center space-x-2">
        <div className="px-3 py-1 bg-black bg-opacity-60 rounded-md text-white text-sm font-medium">
          {quality.toUpperCase()}
        </div>
        <button
          onClick={() => {
            const qualities: Array<'auto' | '1080p' | '720p' | '480p'> = ['auto', '1080p', '720p', '480p'];
            const currentIndex = qualities.indexOf(quality);
            const nextIndex = (currentIndex + 1) % qualities.length;
            setQuality(qualities[nextIndex]);
          }}
          className="p-2 bg-black bg-opacity-60 rounded-md hover:bg-opacity-80 transition-all"
          aria-label="Change quality"
        >
          <Icon name="Cog6ToothIcon" size={20} variant="outline" className="text-white" />
        </button>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex items-center space-x-3">
            <button
              className="p-2 bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-all"
              aria-label="Play/Pause"
            >
              <Icon name="PlayIcon" size={20} variant="solid" className="text-white" />
            </button>
            
            <button
              onClick={toggleMute}
              className="p-2 hover:bg-white hover:bg-opacity-10 rounded-md transition-all"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              <Icon 
                name={isMuted ? 'SpeakerXMarkIcon' : 'SpeakerWaveIcon'} 
                size={20} 
                variant="outline" 
                className="text-white" 
              />
            </button>

            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={handleVolumeChange}
              className="w-24 h-1 bg-white bg-opacity-30 rounded-full appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #2563EB 0%, #2563EB ${volume}%, rgba(255,255,255,0.3) ${volume}%, rgba(255,255,255,0.3) 100%)`
              }}
            />

            <span className="text-white text-sm font-medium min-w-[60px]">
              {isLive ? 'Live' : '00:00 / 45:30'}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              className="p-2 hover:bg-white hover:bg-opacity-10 rounded-md transition-all"
              aria-label="Settings"
            >
              <Icon name="AdjustmentsHorizontalIcon" size={20} variant="outline" className="text-white" />
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-white hover:bg-opacity-10 rounded-md transition-all"
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              <Icon 
                name={isFullscreen ? 'ArrowsPointingInIcon' : 'ArrowsPointingOutIcon'} 
                size={20} 
                variant="outline" 
                className="text-white" 
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;